import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { api, setStoredToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";

// Module-level guard: survives React 18 StrictMode's mount→unmount→remount cycle,
// where useRef state is *not* preserved (each mount creates a fresh ref instance).
// Keyed by token so different tokens can each be consumed exactly once.
const _consumed = new Set();

/** Auto-consumes ?token= on mount, then redirects. */
export default function MagicLinkConsumePage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser, refresh } = useAuth();
  const token = params.get("token") || "";

  const [status, setStatus] = useState(token ? "consuming" : "no-token");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    if (_consumed.has(token)) return; // StrictMode-safe single-fire
    _consumed.add(token);
    (async () => {
      try {
        const { data } = await api.post("/auth/magic-link/consume", { token });
        if (data?.access_token) setStoredToken(data.access_token);
        if (data?.user) setUser(data.user);
        // Re-sync from server so AuthProvider's context is guaranteed in sync
        // before ProtectedRoute runs. Avoids the setUser-vs-navigate race.
        await refresh();
        const u = data?.user;
        if (u?.username) toast.success(`Signed in as @${u.username}`, { duration: 5000 });
        let dest = "/feed";
        if (!u?.username) dest = "/choose-username";
        else if (!u.onboarded) dest = "/onboarding";
        else if (!u.verified) dest = "/verify";
        navigate(dest, { replace: true });
      } catch (err) {
        const httpStatus = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (httpStatus === 400 && /already/i.test(String(detail || ""))) {
          // Token already burnt. Check if we're already authenticated from a prior session.
          // Use api.get directly so a 401 throws and lands us in the failed state.
          try {
            const { data } = await api.get("/auth/me");
            if (data) {
              if (data.user) setUser(data.user); else setUser(data);
              await refresh();
              navigate("/feed", { replace: true });
              return;
            }
          } catch (_) { /* not authed — fall through to failed UI */ }
        }
        setError(detail || "Couldn't sign you in with that link.");
        setStatus("failed");
      }
    })();
  }, [token, navigate, setUser, refresh]);

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6" data-testid="magic-consume-page">
      <div className="max-w-sm text-center">
        <Logo size={40} />
        {status === "consuming" && (
          <div className="mt-8" data-testid="magic-consuming">
            <CircleNotch size={36} className="animate-spin text-primary mx-auto" />
            <h1 className="mt-6 font-heading text-2xl font-black">Signing you in…</h1>
            <p className="mt-2 text-sm text-muted-foreground">Hang tight — almost there.</p>
          </div>
        )}
        {status === "no-token" && (
          <div className="mt-8" data-testid="magic-no-token">
            <Sparkle size={36} className="text-muted-foreground mx-auto" />
            <h1 className="mt-6 font-heading text-2xl font-black">No link</h1>
            <p className="mt-2 text-sm text-muted-foreground">This page needs a magic-link token. Request one from the sign-in screen.</p>
            <Link to="/magic-link" className="mt-6 inline-block px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
              Get a magic link
            </Link>
          </div>
        )}
        {status === "failed" && (
          <div className="mt-8" data-testid="magic-failed">
            <XCircle size={48} weight="fill" className="text-destructive mx-auto" />
            <h1 className="mt-4 font-heading text-2xl font-black">Sign-in failed</h1>
            <p className="mt-2 text-sm text-muted-foreground">{error}</p>
            <Link to="/magic-link" className="mt-6 inline-block px-5 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-semibold">
              Request a new link
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
